/** The Wrapped task on the admin board: pick the period, build, preview, release. */

import { useState, type ChangeEvent } from "react";

import type { WrappedAdmin } from "../../wrapped/types";
import { Def, DefList, Notice, Section, SubSection, formatStamp } from "../shared";

const day = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const label = (date: Date) => date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** "1 Nov 2025 – 31 Oct 2026" for an end date of 2026-11-01. */
function span(end: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  const last = day(end);
  const first = day(end);
  first.setFullYear(first.getFullYear() - 1);
  if (first.getDate() !== last.getDate()) first.setDate(0); // 29 Feb has no twin a year earlier
  last.setDate(last.getDate() - 1);
  return `${label(first)} – ${label(last)}`;
}

const button =
  "rounded border border-[var(--color-line)] bg-[var(--color-surface-2)] px-3 py-1.5 text-xs font-medium disabled:opacity-50";

export function WrappedTaskView({
  data,
  onSave,
  onBuild,
  onRelease,
  onUploadMusic = () => {},
  onRemoveMusic = () => {},
  error = null,
}: {
  data: WrappedAdmin;
  onSave: (end: string, title: string) => void;
  onBuild: () => void;
  onRelease: (released: boolean) => void;
  onUploadMusic?: (file: File) => Promise<unknown> | void;
  onRemoveMusic?: () => void;
  error?: string | null;
}) {
  const [uploading, setUploading] = useState(false);
  const song = data.music ?? null;
  const pickSong = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    void Promise.resolve(onUploadMusic(file)).finally(() => setUploading(false));
  };
  const [end, setEnd] = useState(data.settings.end);
  const [title, setTitle] = useState(data.settings.title);
  const { build, period } = data;
  const running = build.state === "running";
  const unsaved = end !== data.settings.end || title.trim() !== data.settings.title;

  return (
    <Section id="wrapped" title="Wrapped">
      <p className="text-xs text-[var(--color-muted)]">
        The yearly recap. It covers the twelve months before the end date, in the chat's time zone. Nobody but admins
        sees it until you release it.
      </p>
      {error ? <Notice tone="error">{error}</Notice> : null}

      <SubSection id="wrapped-period" title="Period">
        <form
          className="flex flex-wrap items-end gap-3 text-xs"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(end, title.trim());
          }}
        >
          <label className="flex flex-col gap-1">
            End date
            <input
              type="date"
              value={end}
              onChange={(event) => setEnd(event.target.value)}
              className="rounded border border-[var(--color-line)] bg-[var(--color-surface)] px-2 py-1"
            />
          </label>
          <label className="flex flex-col gap-1">
            Title
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="w-28 rounded border border-[var(--color-line)] bg-[var(--color-surface)] px-2 py-1"
            />
          </label>
          <button type="submit" className={button}>
            Save
          </button>
        </form>
        {span(end) ? <p className="text-xs text-[var(--color-muted)]">Covers {span(end)}.</p> : null}
        {unsaved ? <Notice tone="warn">Save first: Build uses the saved settings.</Notice> : null}
      </SubSection>

      <SubSection id="wrapped-build" title="Build">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={button} disabled={running} onClick={onBuild}>
            {running ? "Building…" : "Build"}
          </button>
          {build.state === "running" ? (
            <span className="text-xs text-[var(--color-muted)]">Started {formatStamp(build.started_at)}. Takes about a minute.</span>
          ) : null}
        </div>
        {build.state === "failed" ? <Notice tone="error">The last build failed: {build.error}</Notice> : null}
      </SubSection>

      <SubSection id="wrapped-music" title="Music">
        <p className="text-xs text-[var(--color-muted)]">
          Optional: a song that plays quietly under the story, looping, after the viewer's first tap. Their volume
          slider and mute cover it too. MP3 or M4A, up to 30 MB.
        </p>
        {song ? (
          <p className="text-xs">
            <span className="font-semibold">{song.name}</span>{" "}
            <span className="text-[var(--color-muted)]">· {(song.size / (1 << 20)).toFixed(1)} MB</span>
          </p>
        ) : (
          <p className="text-xs text-[var(--color-muted)]">No song: the story plays its sound effects alone.</p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <label className={`${button} cursor-pointer`}>
            {uploading ? "Uploading…" : song ? "Replace song" : "Upload a song"}
            <input type="file" accept="audio/*,.mp3,.m4a,.aac,.ogg,.wav,.flac" className="sr-only" disabled={uploading} onChange={pickSong} />
          </label>
          {song ? (
            <button type="button" className={button} onClick={onRemoveMusic}>
              Remove
            </button>
          ) : null}
        </div>
      </SubSection>

      <SubSection id="wrapped-release" title="Preview and release">
        {period ? (
          <>
            <p className="text-sm font-semibold">
              {period.title} · {span(period.end)}
            </p>
            <DefList>
              <Def label="Built">{formatStamp(period.built_at)}</Def>
              <Def label="From">{period.source_count.toLocaleString("en-US")} messages</Def>
              <Def label="Time zone">{period.timezone}</Def>
              <Def label="Status">{period.released ? "Released" : "Draft (admins only)"}</Def>
            </DefList>
            <button type="button" className={button} onClick={() => onRelease(!period.released)}>
              {period.released ? "Unrelease" : "Release"}
            </button>
            <ul className="space-y-1 text-xs">
              {data.people.map((person) => (
                <li key={person.id} className="flex items-center justify-between gap-2">
                  <span>
                    {person.name} <span className="text-[var(--color-muted)]">· {person.messages.toLocaleString("en-US")}</span>
                  </span>
                  <a href={`/wrapped?person=${encodeURIComponent(person.id)}`} className="underline">
                    Preview {person.name}
                  </a>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-xs text-[var(--color-muted)]">Nothing built yet.</p>
        )}
      </SubSection>
    </Section>
  );
}
